import React, { FC } from "react";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface PaginationState {
  startRow: number;
  endRow: number;
  totalPages: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
}

interface RidesPaginationProps {
  paginationState: PaginationState;
  currentPerPage: number;
  total: number;
  onChangeRowsPerPage: (event: SelectChangeEvent<string>) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  translator?: any;
}

const RidesPagination: FC<RidesPaginationProps> = ({
  paginationState,
  currentPerPage,
  total,
  onChangeRowsPerPage,
  onPreviousPage,
  onNextPage,
  translator: t
}) => {
  const { startRow, endRow, totalPages, canPreviousPage, canNextPage } =
    paginationState;

  return (
    <Stack
      direction="row"
      spacing={4}
      sx={{
        mt: 2,
        pt: 2,
        borderTop: "1px solid #eee",
        justifyContent: "flex-end",
        alignItems: "center",
        "@container (max-width: 1240px)": {
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center"
        }
      }}
    >
      {/* Row per Page Section */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ color: "#646D7B", fontSize: "14px" }}>
          {t ? t("common.rowsPerPage") : "Rows per page"}:
        </Typography>
        <FormControl sx={{ m: 1, minWidth: 80 }}>
          <Select
            value={String(currentPerPage)}
            onChange={onChangeRowsPerPage}
            autoWidth
            sx={{
              color: "#323946",
              fontSize: "14px",
              fontWeight: 500,
              "& .MuiSvgIcon-root": {
                right: "22px"
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none"
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none"
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none"
              }
            }}
          >
            <MenuItem value={"10"}>10</MenuItem>
            <MenuItem value={"25"}>25</MenuItem>
            <MenuItem value={"50"}>50</MenuItem>
            <MenuItem value={"100"}>100</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Range Information */}
      <Typography
        sx={{ fontSize: "14px", color: "#2E3742", alignSelf: "center" }}
      >
        {t
          ? t("common.toOf", {
              start: startRow,
              end: endRow,
              total: total
            })
          : `${startRow} to ${endRow} of ${total}`}
      </Typography>

      {/* Page Information */}
      <Typography
        sx={{ fontSize: "14px", color: "#2E3742", alignSelf: "center" }}
      >
        {t
          ? t("common.pageOf", {
              current: Math.ceil(startRow / currentPerPage) || 1,
              total: totalPages
            })
          : `Page ${Math.ceil(startRow / currentPerPage) || 1} of ${totalPages}`}
      </Typography>

      {/* Navigation Arrows */}
      <Stack direction="row" spacing={2} justifyContent="center">
        <IconButton
          onClick={onPreviousPage}
          disabled={!canPreviousPage || total === 0}
          sx={{
            padding: "8px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          <ArrowBackIosIcon
            sx={{
              color:
                !canPreviousPage || total === 0
                  ? "#c7cbd1"
                  : "rgba(118, 132, 143, 1)"
            }}
            fontSize="small"
          />
        </IconButton>
        <IconButton
          onClick={onNextPage}
          disabled={!canNextPage || total === 0}
          sx={{
            padding: "8px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          <ArrowForwardIosIcon
            sx={{
              color:
                !canNextPage || total === 0
                  ? "#c7cbd1"
                  : "rgba(118, 132, 143, 1)"
            }}
            fontSize="small"
          />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default RidesPagination;
